/**
 * ParentHelpPage.js — Parent Help & Support mirroring the teen Help Centre:
 * Maya intro banner, big search, coloured category cards, full articles,
 * "Message our team" + My Messages threads.
 */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Mail, Search, Send, Loader2, CheckCircle, MessageCircle, X, ArrowLeft } from 'lucide-react';
import { ParentShell } from '../components/parent/ParentShell';
import { apiClient } from '../api/client';

const CATEGORIES = [
  {
    id: 'signin', name: 'Signing In & Your Account', icon: '🔐',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
    description: 'QR codes, 6-digit codes and account access',
    articles: [
      {
        id: 'how-signin', title: 'How parent sign-in works (QR code + 6-digit code)',
        content: `Your Parent Dashboard is protected without a password — it uses a rotating 6-digit code from an authenticator app on your phone. Here is the full journey:

**Step 1 — Register once.** Go to the Parent registration page and enter your name, email, phone and typed signature.

**Step 2 — Scan your QR code.** After registering you are shown a QR code. Open Google Authenticator, Authy or 1Password on your phone and scan it. This pairs your phone with your HiBFF parent account — you only ever do this once.

**Step 3 — Sign in any time.** From then on, visit Parent sign-in, enter your email, and type the 6-digit code your authenticator app shows. The code changes every 30 seconds, which is what keeps your family's dashboard safe.

**Why no password?** Passwords get reused and leaked. A rotating code from your own phone is stronger and simpler — nothing to remember, nothing to steal.`,
      },
      {
        id: 'lost-phone', title: 'New phone or lost authenticator? Reset your 6-digit code',
        content: `If you changed phones or deleted your authenticator app, your old QR pairing no longer works.

**To re-pair:** message our team from this page (the box at the bottom) and we will send a one-time link that re-issues a fresh QR code. Scan the new QR with your new phone and you are back in.

Nothing is lost — your teens, Charter and progress data are untouched. Only the sign-in pairing is refreshed.`,
      },
      {
        id: 'add-parent', title: 'Adding a second parent or guardian',
        content: `From your Dashboard, use **Invite other parents** — enter their email or phone number and they receive their own secure registration link.

They complete the same one-time QR pairing and get their own login. Both of you see the same family view: teens, progress, Hearts and Vault.`,
      },
    ],
  },
  {
    id: 'privacy', name: 'Privacy & Trust', icon: '🛡️',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    description: 'What you can see — and what stays private',
    articles: [
      {
        id: 'why-private', title: 'Why you can never read your teen\u2019s conversations',
        content: `By design — and by promise. The Charter you signed guarantees **Complete Privacy**, and our Privacy Shield enforces it in code: every conversation endpoint returns a hard block to parent sessions. It is not a policy that could be quietly changed; it is built into the server.

**What you DO see:** how often your teen practises (days active, streak, last seen), Hearts earned and spent, badges, completed challenges, and everything in their 180-day Vault.

Teens who know their practice is truly private practise more honestly — and improve faster. That privacy is the engine of the whole product.`,
      },
      {
        id: 'safety-escalation', title: 'What happens if my teen is ever unsafe?',
        content: `Nine safety domains are filtered in real time before any AI ever responds — self-harm, violence, exploitation and more. The AvA tutors are also topic-locked and cannot be steered off their specialist subjects.

If our team identifies a genuine safety concern, **a human contacts you directly**. That is the one exception to conversation privacy, and it always goes through people, never automated snooping.

Your teen also has a Safety Net on their dashboard with local emergency numbers, and a "Maya Helps" button that reaches a real human on our team.`,
      },
    ],
  },
  {
    id: 'rewards', name: 'Hearts, Vault & NFTs', icon: '💜',
    color: 'bg-purple-100 text-purple-700 border-purple-300',
    description: 'The reward system, the 180-day Vault and the pivot',
    articles: [
      {
        id: 'hearts-explained', title: 'Hearts — what they are and how your teen earns them',
        content: `Hearts are HiBFF's encouragement currency. Teens earn them by **showing up**: completing challenges, keeping practice streaks, and working with their AvA tutors.

Hearts are spent in the Hearts Store on practice credits, streak freezes and collectibles. Big-ticket spends route to you for approval — you will see them waiting on your Hearts page.

Per the Charter you signed: Hearts belong to your teen and can never be confiscated.`,
      },
      {
        id: 'pivot-point', title: 'The pivot point — from virtual badges to real NFTs',
        content: `Your teen's earned badges build toward the **pivot point**: the moment their best collectible is minted as a real NFT on the Polygon blockchain via Crossmint.

**You will get a text message the moment it happens** — it is a milestone worth celebrating at the dinner table.

After minting, the NFT sits in their 180-day Vault. When the countdown reaches zero the full artwork is revealed, and your teen chooses: keep it forever, or trade it with 100% of proceeds routed to their in-app wallet.`,
      },
    ],
  },
  {
    id: 'billing', name: 'Plans & Billing', icon: '💳',
    color: 'bg-sky-100 text-sky-700 border-sky-300',
    description: 'Subscriptions, cancellations and receipts',
    articles: [
      {
        id: 'cancel-anytime', title: 'Cancelling, pausing and what your teen keeps',
        content: `You are never locked in — three options, all self-serve from **Settings → Subscription** (no calls, no emails, no hoops):

**1. Cancel Subscription** — one tap in Settings → Subscription → "Cancel Subscription". Your card is never charged again, you and your teen keep full access until the end of the paid period, then the account moves to the free First Steps tier automatically. Changed your mind? An "Undo" button appears until the period ends.

**2. Pause for 90 days** — a cooling-off break. No charges while paused; everything is exactly where you left it when you resume.

**3. Pause indefinitely** — no time limit, no deadline. Your teen's AvAs, conversations and progress are preserved for as long as you stay paused — resume whenever you're ready.

**Your teen keeps everything they earned** — Hearts, badges and Vault items are theirs regardless of your subscription status.

Receipts and plan changes are emailed to you automatically, and we send a reminder email + text before every renewal.`,
      },
      {
        id: 'who-billed', title: 'Who gets billed — you or your teen?',
        content: `Always you. Teens never see prices, card forms or billing of any kind anywhere in their app. All money matters live exclusively on your Parent Dashboard.`,
      },
    ],
  },
];

const ALL_ARTICLES = CATEGORIES.flatMap(c => c.articles.map(a => ({ ...a, category: c })));

const ArticleBody = ({ content }) => (
  <div className="text-sm text-gray-700 leading-relaxed space-y-3">
    {content.split('\n\n').map((p, i) => (
      <p key={i}>{p.split('**').map((part, j) => (j % 2 === 1 ? <strong key={j}>{part}</strong> : part))}</p>
    ))}
  </div>
);

const ParentHelpPage = () => {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState(null);
  const [openArticle, setOpenArticle] = useState(null);
  const [msg, setMsg] = useState('');
  const [sendState, setSendState] = useState('idle');
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [threads, setThreads] = useState(null);

  const openThreads = () => {
    setThreadsOpen(true);
    setThreads(null);
    apiClient.request('/api/help/mine').then(d => setThreads(d?.requests || [])).catch(() => setThreads([]));
    apiClient.request('/api/help/mine/seen', { method: 'POST' }).catch(() => {});
  };

  useEffect(() => {
    if (new URLSearchParams(location.search).get('messages')) openThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendToTeam = async () => {
    if (!msg.trim()) return;
    setSendState('sending');
    try {
      await apiClient.request('/api/help/request', { method: 'POST', body: JSON.stringify({ message: msg.trim() }) });
      setSendState('sent');
      setMsg('');
      setTimeout(() => setSendState('idle'), 3500);
    } catch (e) {
      alert(e?.message || 'Could not send.');
      setSendState('idle');
    }
  };

  const q = query.trim().toLowerCase();
  const searchResults = q ? ALL_ARTICLES.filter(a => (a.title + ' ' + a.content).toLowerCase().includes(q)) : [];
  const cat = CATEGORIES.find(c => c.id === activeCat);

  return (
    <ParentShell activeId="help" title="Help & Support">
      <div className="px-6 lg:px-10 py-8 lg:py-10 max-w-5xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Help &amp; Support</h2>
          <p className="text-base text-gray-600">Everything a parent or guardian needs — and a human when you want one.</p>
        </div>

        {/* Maya intro banner — mirrors the teen Help Centre */}
        <div className="max-w-3xl mx-auto mb-8 flex flex-col sm:flex-row items-center gap-6 bg-orange-50 border border-orange-200 rounded-3xl p-6 text-left" data-testid="parent-maya-intro">
          <img src="/clare-ava.png" alt="Maya" className="w-32 h-32 rounded-3xl object-cover shadow-lg flex-shrink-0" />
          <div>
            <p className="text-lg font-bold text-gray-900 mb-2">Hi! I&apos;m Maya — your family&apos;s guide.</p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Search below or browse the four sections — I&apos;ve organised everything parents ask us.
              Still stuck? Write to us in <strong>&quot;Message our team&quot;</strong> at the bottom and a human replies personally —
              the answer lands in <strong>My Messages</strong> right here on this page.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <Search size={20} className="absolute left-4 top-3.5 text-gray-400" />
            <input
              value={query} onChange={e => setQuery(e.target.value)}
              placeholder="What do you need help with?"
              className="w-full pl-12 pr-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
              data-testid="parent-help-search"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="max-w-2xl mx-auto mt-4 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" data-testid="parent-help-search-results">
              {searchResults.slice(0, 6).map(a => (
                <button key={a.id}
                  onClick={() => { setActiveCat(a.category.id); setOpenArticle(a.id); setQuery(''); }}
                  className="w-full p-4 text-left hover:bg-purple-50 border-b border-gray-100 last:border-b-0 flex items-start justify-between gap-3">
                  <div>
                    <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded mb-1.5 ${a.category.color}`}>{a.category.name}</span>
                    <p className="font-semibold text-gray-900">{a.title}</p>
                  </div>
                  <ChevronRight size={18} className="text-gray-400 flex-shrink-0 mt-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category cards OR open category */}
        {!cat ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10" data-testid="parent-help-categories">
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => { setActiveCat(c.id); setOpenArticle(c.articles[0].id); }}
                className={`p-6 rounded-xl border-2 shadow-md hover:shadow-lg transition-all text-left ${c.color}`}
                data-testid={`parent-help-cat-${c.id}`}>
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-3 text-2xl">{c.icon}</div>
                <h3 className="font-bold text-lg mb-1.5">{c.name}</h3>
                <p className="text-sm opacity-80 mb-3">{c.description}</p>
                <span className="flex items-center gap-1.5 text-sm font-semibold">Learn more <ChevronRight size={14} /></span>
              </button>
            ))}
          </div>
        ) : (
          <div className="mb-10" data-testid="parent-help-category-view">
            <button onClick={() => { setActiveCat(null); setOpenArticle(null); }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700 hover:text-purple-900 mb-4"
              data-testid="parent-help-back">
              <ArrowLeft size={15} /> All topics
            </button>
            <div className="flex items-center gap-3 mb-4">
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl border-2 ${cat.color}`}>{cat.icon}</span>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{cat.name}</h3>
                <p className="text-sm text-gray-500">{cat.description}</p>
              </div>
            </div>
            <ul className="space-y-3">
              {cat.articles.map(a => {
                const open = openArticle === a.id;
                return (
                  <li key={a.id} className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                    <button onClick={() => setOpenArticle(open ? null : a.id)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50"
                      data-testid={`parent-article-${a.id}`}>
                      <span className="font-semibold text-gray-900">{a.title}</span>
                      <ChevronDown size={18} className={`text-gray-400 transition flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
                    </button>
                    {open && <div className="px-5 pb-6" data-testid={`parent-article-body-${a.id}`}><ArticleBody content={a.content} /></div>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Message our team */}
        <div className="max-w-3xl mx-auto rounded-3xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 p-6 mb-8" data-testid="parent-maya-helps">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h3 className="font-bold text-gray-900 text-lg">Message our team</h3>
            <button onClick={openThreads}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-700 hover:text-orange-900"
              data-testid="parent-my-messages-btn">
              <MessageCircle size={16} /> My Messages
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">A human reads and replies personally — the answer lands in My Messages above.</p>
          {sendState === 'sent' ? (
            <div className="flex items-center gap-2 text-emerald-700 font-semibold py-3" data-testid="parent-maya-sent">
              <CheckCircle size={18} /> Sent — we&apos;ll reply here soon.
            </div>
          ) : (
            <div className="flex gap-2">
              <textarea rows={2} value={msg} onChange={e => setMsg(e.target.value)}
                placeholder="Ask us anything about your teen's HiBFF journey…"
                className="flex-1 px-4 py-3 rounded-2xl border-2 border-orange-200 focus:border-orange-400 outline-none resize-none bg-white"
                data-testid="parent-maya-input" />
              <button onClick={sendToTeam} disabled={sendState === 'sending' || !msg.trim()}
                className="px-5 rounded-2xl bg-orange-500 hover:bg-orange-400 text-white font-bold disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="parent-maya-send">
                {sendState === 'sending' ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          )}
        </div>

        {/* Contact banner — mirrors teen page */}
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-8 text-center">
          <Mail size={36} className="mx-auto text-purple-600 mb-3" />
          <h3 className="text-lg font-bold text-gray-900 mb-1">Want the founder?</h3>
          <p className="text-sm text-gray-700">
            &quot;Message Robert Personally&quot; in the top-right lands directly on the founder&apos;s desk — no queue, no gatekeeper.
          </p>
        </div>

        {/* My Messages threads modal */}
        {threadsOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" data-testid="parent-threads-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">My Messages</h3>
                <button onClick={() => setThreadsOpen(false)} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400" data-testid="parent-threads-close">
                  <X size={18} />
                </button>
              </div>
              <div className="overflow-y-auto flex-1 space-y-4 pr-1">
                {threads === null ? (
                  <div className="py-10 text-center"><Loader2 size={24} className="animate-spin mx-auto text-orange-400" /></div>
                ) : threads.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No messages yet — write one above and it will appear here.</p>
                ) : threads.map(t => (
                  <div key={t.request_id} className="border border-gray-100 rounded-xl p-3">
                    <p className="text-[11px] text-gray-400 mb-1.5">{new Date(t.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    <div className="bg-gray-100 rounded-xl rounded-tr-sm px-3 py-2 ml-8">
                      <p className="text-xs font-semibold text-gray-500 mb-0.5">You</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{t.message}</p>
                    </div>
                    {t.reply ? (
                      <div className="rounded-xl rounded-tl-sm px-3 py-2 mr-8 mt-2 bg-orange-50">
                        <p className="text-xs font-semibold mb-0.5 text-orange-600">HiBFF Team</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{t.reply}</p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic mt-2 mr-8">Not answered yet — the reply will appear here.</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </ParentShell>
  );
};

export default ParentHelpPage;
